import React, { useEffect, useMemo } from 'react';
import { useToggle, useAsync, } from 'react-use';
import { Nav, NavItem, NavLink, Button } from 'reactstrap';
import { toast } from 'react-toastify';
import { sortBy, isEmpty, keyBy, get, omitBy, isUndefined, pick, } from 'lodash';
import { parse as parseDate, format as formatDate, addMonths, addDays, startOfDay, endOfDay, } from 'date-fns';
import numeral from 'numeral';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import qs from 'qs';

import { canUpdateAgent, } from '../../shared/abilities';
import { routes, } from '../../shared/config';
import { errorMessages as userErrorMessages } from '../../shared/models/user';
import { fields as agentContentProductSettingFields, } from '../../shared/models/agentContentProductSetting';
import { chart, } from '../../shared/models/order';
import firebase, { functions } from '../../firebase';
import { areaFromPostalCode } from '../../shared/models/setting';
import useFirebaseUser from '../hooks/useFirebaseUser';
import useQueryParams from '../hooks/useQueryParams';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useCollectionSubscriptionInTenant from '../hooks/useCollectionSubscriptionInTenant';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useDocumentsFetch from '../hooks/useDocumentsFetch';
import TenantUserPage from '../hocs/TenantUserPage';
import ModelFormModal from '../modals/ModelFormModal';
import AdminAgentHeader from '../AdminAgentHeader';
import QueryDateSelector from '../QueryDateSelector';
import AdminHelp from '../AdminHelp';
import AddInTenantButton from '../AddInTenantButton';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import ExportButton from '../ExportButton';
import ImportButton from '../ImportButton';
import TrendCharts from '../TrendCharts';
import ComparisonCharts from '../ComparisonCharts';
import QuerySelector from '../QuerySelector';
import { agentUserFields } from '../../shared/models/user';

const { entries, keys } = Object;
const fetchSalesStatData = functions.httpsCallable('fetchSalesStatData');
const db = firebase.firestore();
const tabs = {
  trend: { label: 'トレンド', },
  comparison: { label: '期間比較', },
};
const dimensions = pick(chart.dimensions, ['route', 'area', 'pic']);

export default TenantUserPage(function SalesStat(props) {
  const { user, tenant, location, history } = props;
  const tenantUser = useDocumentSubscription(user != null && tenant?.ref.collection('tenantUsers').doc(user?.id), [tenant != null, user != null]);
  const areaSetting = useDocumentSubscription(db.collection('settings').doc(`${tenant?.id}__area`));
  const now = new Date();
  const queryParams = useQueryParams();
  const {
    tab = 'trend',
    startOn: startOnString = formatDate(addDays(new Date(), -30), 'yyyy-MM-dd'),
    endOn: endOnString = formatDate(addDays(new Date(), -1), 'yyyy-MM-dd'),
    comparedStartOn: comparedStartOnString,
    comparedEndOn: comparedEndOnString,
  } = queryParams;
  const startOn = new Date(startOnString);
  const endOn = new Date(endOnString);
  const comparedStartOn = comparedStartOnString ? new Date(comparedStartOnString) : null;
  const comparedEndOn = comparedEndOnString ? new Date(comparedEndOnString) : null;
  const products = useCollectionSubscriptionInTenant(db.collection('products').orderBy('code'));
  const productsById = useMemo(() => keyBy(products, 'id'), [products]);
  const productTypes = useCollectionSubscriptionInTenant(db.collection('productTypes').orderBy('index'));
  const fetchOrders = async (startOn, endOn) => {
    if(!startOn || !endOn) return [];

    const { data } = await fetchSalesStatData({ tenantId: tenant.id, startOn: startOfDay(startOn).toISOString(), endOn: endOfDay(endOn).toISOString(), });
    return data.orders.map((order) => {
      const createdAt = new Date(order.createdAt);
      return { ...order, createdAt: { toDate: _ => createdAt, } };
    });
  };
  const { value: orders } = useAsync(() => fetchOrders(startOn, endOn), [startOnString, endOnString]);
  const { value: comparedOrders } = useAsync(() => fetchOrders(comparedStartOn, comparedEndOn), [comparedStartOnString, comparedEndOnString]);
  const hasProductTypeFilter = !isEmpty(tenantUser?.salesStatFilterProductTypes);
  const hasRouteFilter = !isEmpty(tenantUser?.salesStatFilterRoutes);
  const canChangeProductTypeFilter = user.role === 'admin' || !hasProductTypeFilter;
  const canChangeRouteFilter = user.role === 'admin' || !hasRouteFilter;
  const activeRoutes = queryParams.routes ?? tenantUser?.salesStatFilterRoutes;
  const activeProductTypes = queryParams.productTypes ?? tenantUser?.salesStatFilterProductTypes;

  const filterRows = (orders) => {
    const rows = (orders ?? []).map((order) => {
      const { destinationPostalCode, contactorPostalCode, } = order;
      const area = areaFromPostalCode(contactorPostalCode || destinationPostalCode, areaSetting);
      return { order, area, };
    });
    let filteredRows = rows;
    if (!isEmpty(activeProductTypes)) {
      filteredRows = filteredRows.filter((_) => (_.order.orderItems)?.some(_ => productsById[_.productId]?.productTypeIds?.some(_ => activeProductTypes.includes(_)) && productsById[_.productId]?.isBody));
    }
    if(!isEmpty(activeRoutes)) {
      filteredRows = filteredRows.filter((_) => activeRoutes.includes(get(_, 'order.route')));
    }
    return filteredRows;
  };
  const filteredRows = useMemo(() => filterRows(orders), [orders, areaSetting, productsById, activeProductTypes, activeRoutes]);
  const filteredComparedRows = useMemo(() => filterRows(comparedOrders), [comparedOrders, areaSetting, productsById, activeProductTypes, activeRoutes]);
  const buttons = [
    { label: '1年前', monthsAgo: 12, },
    { label: '半年前', monthsAgo: 6, },
    { label: '前月', monthsAgo: 1, },
  ].map(_ => ({ label: _.label, dates: () => [addMonths(startOn, -_.monthsAgo), addMonths(endOn, -_.monthsAgo)] }));
  const allProductTypeOptions = productTypes.map(_ => ({ label: _.name, value: _.id }));
  const allRouteOptions = [...routes, 'LIONS EC'].map(_ => ({ label: _, value: _, }));
  const productTypeOptions = canChangeProductTypeFilter ? allProductTypeOptions : allProductTypeOptions.filter(_ => tenantUser?.salesStatFilterProductTypes?.includes(_.value));
  const routeOptions = canChangeRouteFilter ? allRouteOptions : allRouteOptions.filter(_ => tenantUser?.salesStatFilterRoutes?.includes(_.value));
  return (user.role === 'admin' || !!tenantUser?.salesStatReadble) && (
    <div>
      <div className='sales-stat ontainer-fluid py-5 position-relative'>
        <div className='d-flex justify-content-center align-items-center mb-3 gap-2'>
          <h4>販売統計</h4>
          <AdminHelp disabledPreview user={user} helpKey={`${tenant?.id}__sales-stat`} title="販売統計ヘルプ" label="" icon="question-circle" buttonProps={{ size: 'sm' }} />
        </div>
        <div className='d-flex align-items-end flex-wrap gap-2'>
          <QueryDateSelector
            paramName='startOn'
            label='注文開始日'
            history={history}
            location={location}
            defaultValue={startOn}
            style={{ width: 150 }}
          />
          <QueryDateSelector
            paramName='endOn'
            label='注文終了日'
            history={history}
            location={location}
            defaultValue={endOn}
            style={{ width: 150 }}
          />
        </div>
        <div className='mt-2 d-flex align-items-end flex-wrap gap-2'>
          <QuerySelector
            paramName='productTypes'
            width={400}
            isMulti
            options={productTypeOptions}
            label='商品種別で絞込み'
          />
          <QuerySelector
            paramName='routes'
            width={400}
            isMulti
            options={routeOptions}
            label='経路'
          />
        </div>
        <Nav tabs className="mt-3">
          {
            entries(tabs).map(([name, { label }]) => {
              return (
                <NavItem key={name}>
                  <NavLink
                    tag={Link}
                    className={classnames({ active: tab === name })}
                    to={`${location.pathname}?${qs.stringify({ ...queryParams, tab: name, })}`}
                  >
                    {label}
                  </NavLink>
                </NavItem>
              );
            })
          }
        </Nav>
        <div>
          {
            ({
              trend: _ => (
                <TrendCharts {...{ filteredRows, startOn, endOn, productsById, dimensions, activeProductTypes }} />
              ),
              comparison: _ => (
                <ComparisonCharts {...{ filteredRows, filteredComparedRows, startOn, endOn, comparedStartOn, comparedEndOn, productsById, dimensions, history, location, activeProductTypes }} />
              ),
            })[tab]()
          }
        </div>
      </div>
    </div>
  );
});
